'use client';

import { Form } from '@/src/components/external/Form/Form';
import React, { useState, useEffect } from 'react';
import { UseFormReturn, useFormContext } from 'react-hook-form';
import { Box, Stack, Typography } from '@mui/material';
import { deletePlayerSchema, deletionRequest } from '@/src/network/api/gdpr/delete/deletionRequest';
import { DeletionForm } from './DeletionForm';
import UIDialog from '@/src/components/external/UIDialog/UIDialog';
import { CheckCircleOutline } from '@mui/icons-material';

interface IPlayerDeletionProps {
    open: boolean;
    handleClose: (...args: any[]) => void;
}

/**
 * The primary deletion form component
 * @returns Component
 */
export const PlayerDeletion = (props: IPlayerDeletionProps) => {

    // STATE
    const [open, setOpen] = useState(props.open !== undefined ? props.open : false);

    // Effects
    useEffect(() => {
        if (props.open !== undefined) {
            setOpen(props.open);
        }
    }, [props.open]);

    // Handlers
    const handleClose = () => {
        setOpen(false);
        props.handleClose();
    }

    // HANDLERS
    /**
     * Handles posting player details when submitting player deletion request
     *
     * @param param.player - player name
     * @param param.team - team name
     * @param param.client - client name
     * @param formInstance - reference to the current form instance
     */
    const handleSubmit = async ({player, team, client}: { player: string, team: string, client: string }, formInstance?: UseFormReturn) => {
        if(formInstance !== undefined) {
            formInstance.clearErrors();
        }
        await deletionRequest({player, team, client}).then((res) => {
                if (!res?.success || res?.errors) {
                    if (res?.errors) {
                        if(formInstance) {
                            formInstance.setError('api', { message: "failed to submit form", type: 'api' })
                        }
                    } else {
                        if(formInstance) {
                            formInstance.setError('api', { message: "unknown error has occurred", type: 'api' })
                        }
                    }
                }
        }).catch((err) => {

                if(process.env.DEBUG) {
                    console.error('ERR', err);
                }

                if(formInstance) {
                    formInstance.setError('api', { message: "unknown error has occurred", type: 'api' })
                }
            })
    }

    return (
        <UIDialog
            isOpen={open}
            title={'Player Account Deletion Request'}
            content={
                <Form onSubmit={handleSubmit} schema={deletePlayerSchema} styles={{ height: '100%', width: '100%' }}>
                    <DeletionContent />
                </Form>
            }
            onClose={handleClose}
            cancelButtonText={'Close'}
        />
    )

}

/**
 * Switches between displaying deletion form, or success state
 *
 * @returns Component
 */
const DeletionContent = () => {

    // FORM
    const { formState: { isSubmitSuccessful } } = useFormContext();

    if(isSubmitSuccessful) {
        return (
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'100%'} gap={2}>
                <Stack alignItems={'center'} gap={2}>
                    <Typography variant={'h2'}>
                        <CheckCircleOutline fontSize={'inherit'} color={'success'}/>
                    </Typography>
                    <Typography variant='h5' textAlign={'center'}>Your deletion request has been submitted.<br /> Please wait a few days as we process your deletion request.</Typography>
                </Stack>
            </Box>
        )
    }

    return (
        <>
            <DeletionForm />
        </>

    )
}
